/**
 * Real `verifyById` — the server's `/gi/{id}/verify` returns 204 on
 * success and 422 on failure, with no body on either branch. The
 * UI consumes a `VerificationResult` that carries `manifest`,
 * `signed_entity`, `lineage`, and a `findings: VerificationFinding[]`
 * list. We compose that result from a detail + lineage fetch and a
 * synthesized pass/fail finding.
 *
 * This shape match is deliberately conservative: the UI's Verify
 * panel already knows how to render a result with a single pass or
 * fail row. When the server grows a richer verification payload, we
 * lift individual findings from it here.
 */

import type {
  EntityId,
  VerificationFinding,
  VerificationResult,
} from '../types';
import { ApiHttpError, apiFetch } from '../http';
import { getGiDetail } from './gi';

export async function verifyById(root_id: EntityId): Promise<VerificationResult | null> {
  const detail = await getGiDetail(root_id);
  if (!detail) return null;

  let serverOk: boolean;
  let failureDetail: string | undefined;

  try {
    await apiFetch<void>(`/gi/${encodeURIComponent(root_id)}/verify`);
    serverOk = true;
  } catch (err) {
    if (err instanceof ApiHttpError) {
      if (err.notFound) return null;
      if (err.status === 422) {
        serverOk = false;
        failureDetail = err.detail ?? err.title;
      } else {
        throw err;
      }
    } else {
      throw err;
    }
  }

  const findings: VerificationFinding[] = [];
  if (serverOk) {
    findings.push({ kind: 'signature_valid' });
    findings.push({ kind: 'chain_ok' });
    findings.push({ kind: 'contract_hash_ok' });
    findings.push({ kind: 'wasm_hash_ok' });
    findings.push({ kind: 'timestamp_ok', at: detail.manifest.created_at });
  } else {
    findings.push({
      kind: 'signature_invalid',
      reason: failureDetail ?? 'Server rejected the signature or chain.',
    });
  }

  return {
    ok: serverOk,
    manifest: detail.manifest,
    signed_entity: detail.signed_entity,
    contract: detail.contract,
    lineage: detail.lineage,
    findings,
  };
}
